<script setup lang="ts">
import { ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import yup from '@/plugins/yup'
import InputGroup from '@/components/Auths/InputGroup.vue'
import ButtonLoading from '@/components/Buttons/ButtonLoading.vue'
import { ValidationError } from 'yup'
import { useErrorStore } from '@/store/error'
import { InputFromField } from '@/interfaces/components/inputFromField'
import OutlineButton from '@/components/Globals/Buttons/OutlineButton.vue'
import { useAlarmProfileStore } from '@/store/alarmProfile'
import { IEmptyMixerAlarmSensor } from '@/interfaces/entities/alarm_profile'

const { t } = useI18n()
const profileStore = useAlarmProfileStore()
const isLoading = ref(false)

const emit = defineEmits(['cancel'])

const props = defineProps<{
  currentProfile: IEmptyMixerAlarmSensor | null,
  formType: 'create' | 'edit',
}>()

const currentSettings = props.currentProfile?.settings

const inputs: Ref<InputFromField[]> = ref([
  {
    label: 'Nome',
    placeholder: 'alarms.inform_profile_name',
    value: props.formType === 'edit' ? props.currentProfile?.name ?? '' : '',
  },
  {
    label: t('alarms.empty_mixer.multiplier'),
    placeholder: 'alarms.current_message',
    value: props.formType === 'edit' ? currentSettings?.empty_mixer_multiplier ?? '' : ''
  },
  {
    label: t('alarms.empty_mixer.variation_threshold'),
    placeholder: 'alarms.current_message',
    value: props.formType === 'edit' ? currentSettings?.empty_mixer_variation_threshold ?? '' : ''
  },
  {
    label: t('alarms.empty_mixer.activation_setpoint'),
    placeholder: 'alarms.current_message',
    value: props.formType === 'edit' ? currentSettings?.empty_mixer_activation_setpoint ?? '' : ''
  },
  {
    label: t('alarms.empty_mixer.min_peak_duration_minutes'),
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.empty_mixer_min_peak_duration_minutes ?? '' : ''
  },
  {
    label: t('alarms.empty_mixer.time_limit_minutes'),
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.empty_mixer_time_limit_minutes ?? '' : ''
  }
])

const alarmSettingsSchema = yup.object({
  name: yup
  .string()
  .required('O campo nome é obrigatório'),
  empty_mixer_multiplier: yup
    .number()
    .typeError('O campo de duração de multiplicador de misturador vazio deve ser um número')
    .required('O campo de duração de multiplicador de misturador vazio é obrigatório')
    .min(0, 'O tempo de duração não pode ser menor que zero'),
  empty_mixer_variation_threshold: yup
    .number()
    .typeError('O campo de variação de threshold de misturador vazio deve ser um número')
    .required('O campo de variação de threshold de misturador vazio é obrigatório')
    .min(0, 'O campo de variação de threshold de misturador vazio não pode ser menor que zero'),
  empty_mixer_activation_setpoint: yup
    .number()
    .typeError('O campo de ativação de setpoint de misturador vazio deve ser um número')
    .required('O campo de ativação de setpoint de misturador vazio é obrigatório')
    .min(0, 'O campo de ativação de setpoint de misturador vazio não pode ser menor que zero'),
  empty_mixer_min_peak_duration_minutes: yup
    .number()
    .typeError('O campo de duração de pico de misturador vazio deve ser um número')
    .required('O campo de duração de pico de misturador vazio é obrigatório')
    .min(0, 'O campo de duração de pico de misturador vazio não pode ser menor que zero'),
  empty_mixer_time_limit_minutes: yup
    .number()
    .typeError('O campo de limite de tempo de misturador vazio deve ser um número')
    .required('O campo de limite de tempo de misturador vazio é obrigatório')
    .min(0, 'O campo de limite de tempo de misturador vazio não pode ser menor que zero'),
})


async function handleSubmit() {
  const values: IEmptyMixerAlarmSensor = {
    name: String(inputs.value[0].value),
    empty_mixer_multiplier: inputs.value[1].value,
    empty_mixer_variation_threshold: inputs.value[2].value,
    empty_mixer_activation_setpoint: inputs.value[3].value,
    empty_mixer_min_peak_duration_minutes: inputs.value[4].value,
    empty_mixer_time_limit_minutes: inputs.value[5].value,
  }
  try {
    await alarmSettingsSchema.validate(values, { abortEarly: false })
    isLoading.value = true
    if(props.formType === 'create'){
      await profileStore.createEmptyMixerAlarmProfile(values)
    } else if(props.formType === 'edit' && props.currentProfile){
      await profileStore.editEmptyMixerAlarmProfile(props.currentProfile.id, values)
    }
    isLoading.value = false
    emit('cancel')
  } catch (err) {
    if (err instanceof ValidationError) {
      useErrorStore().clearErrors();
      useErrorStore().setErrors('Erro ao criar perfil: ', err.errors)
    }
  }
}
</script>

<template>
  <div class="border border-stroke bg-gray-2 p-5 shadow-sm">
    <div class="flex flex-wrap gap-4">
      <span v-if="props.formType ==='create'" class="w-full text-base font-medium text-gray-500 mb-1">
        {{ t('alarms.empty_mixer.create_profile') }}
      </span>
      <div v-if="props.formType === 'edit'" class="w-full flex justify-items-center">
        <span class="w-full text-base font-medium text-gray-500 mb-1">
          {{ "Editar perfil: " + (props.currentProfile?.name || '') }}
        </span>
      </div>
      <div class="w-full border-t border-stroke pt-1"></div>
      <template v-for="(input, index) in inputs" :key="index">
        <div class="min-w-[200px] w-full">
          <InputGroup v-model="input.value" :label="t(input.label)" :placeholder="t(input.placeholder)" />
        </div>
      </template>

      <div class="flex w-full justify-end gap-2">
        <OutlineButton 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !border-stroke !bg-white !shadow-sm hover:!bg-gray-2 !flex !items-center !justify-center whitespace-nowrap"
          @click="emit('cancel')"
          label="Cancelar" />
        <ButtonLoading 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !text-white !border-blue-600 !bg-blue-600 !shadow-sm hover:!bg-blue-700 !flex !items-center !justify-center whitespace-nowrap"
          :isLoading="isLoading"
          @submit="handleSubmit"
          label="Salvar" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  :deep(label) {
    color: #64748b;
    font-size: 0.875rem;
  }
  :deep(input) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
</style>